<script>
    import { onMount } from "svelte";

    import { isEqual } from "lodash";

    import { apiMethods } from "../../../../../Services/index.svelte";

    import {
        user,
        mbsItemsLogic,
        asaItemsLogic,
        amaUnitValue,
        asaAllItemsList,
        mbsAllItemsList,
    } from "../../../../../stores";

    import {
        SvelteTable,
        NotificationModal,
        CardLayout,
        PageLoading,
    } from "../../../../../Components/Common/index.svelte";

    import {
        getAmountInCurrency,
        getBillingItemCodeType,
        getBillingRuleType,
        getNumber,
        getOOPType,
        getText,
        isObjectEmpty,
        showError,
        showException,
    } from "../../../../../Utils/utilFunctions.svelte";

    import {
        addIcon,
        deleteIcon,
        editIcon,
        insuranceTypesMayAllowAmaBillingRule,
        insuranceTypesNotAllowBillingRule,
        unknownPatientInsuranceType,
    } from "../../../../../Utils/utilConstants.svelte";

    import {
        generateLatestBillingDetails,
        getMBSRebateUnits,
        getSumOfColumnValues,
        updateBillingRuleTotalFee,
        updateEncounterLatestBillingData,
    } from "../../../../PatientEncounter/helpers/encounterHelper.svelte";

    import CodeSelectorMbs from "../../../../PatientEncounter/components/codeSelector/CodeSelectorMBS.svelte";
    import CodeSelectorAsa from "../../../../PatientEncounter/components/codeSelector/CodeSelectorASA.svelte";
    import CodeSelectorFreeText from "../../../../PatientEncounter/components/codeSelector/CodeSelectorFreeText.svelte";

    import PatientEncounterModifyItemModal from "../../../../PatientEncounter/components/PatientEncounterModifyItemModal.svelte";

    import PatientEncounterAddBillingRuleModal from "../../../../PatientEncounter/components/PatientEncounterAddBillingRuleModal.svelte";
    import { fetchAndSyncItemsRebate } from "../../../../PatientEncounter/helpers/mbsHelper.svelte";
    import { cacheAsaItems, cacheMbsItems } from "../../../../../Utils/utilCache.svelte";
    import { presetRuleType } from "../../../../../Utils/utilEnums.svelte";
    import { getSelectedPresetApplicableRuleForEncounter } from "../../../../Presets/helpers/presetsHelper.svelte";

    export let patientEncounterDetails = {};
    export let latestObjDetails = {};
    export let itemsData = []; // mbsData or asaData
    export let billingData = []; // quotesData
    export let doctorProviders = [];
    export let __BackupItemsData__ = [];
    export let isRegenerating = false;
    export let itemType = "fee estimate";
    export let reloadBillingDetails = () => {};
    export let regenerateQuote = () => {};
    export let reloadBillingDetailsAndUpdateDefaultItems = () => {};

    $: itemsNotSaved = !isEqual(itemsData, __BackupItemsData__);

    $: if (itemsNotSaved) displayItemsData = _syncLatestDataItemsWithLogic();

    $: isBillingDetailsUsingAsa =
        latestObjDetails?.billingCodesType &&
        getBillingItemCodeType(latestObjDetails.billingCodesType) === "ASA";

    $: isBillingDetailsUsingFreeText =
        latestObjDetails?.billingCodesType &&
        getBillingItemCodeType(latestObjDetails.billingCodesType) ===
            "FreeText";

    $: allowAsaCodes = _checkAllowAsaCodes(patientEncounterDetails);

    $: allowFreeTextCodes =
        patientEncounterDetails?.patient?.insuranceType == 15;

    $: columns = isBillingDetailsUsingFreeText
        ? [...FreeTextColumns]
        : isBillingDetailsUsingAsa
          ? [...AsaColumns]
          : [...MbsColumns];

    $: showAddItemIcon = !isObjectEmpty(latestObjDetails);

    // property determined to hide switch item type between mbs & asa when related quote/invoice is valid
    $: hasValidBillingData =
        billingData.length &&
        billingData.some(
            (bd) =>
                bd.patientEncounterBillingDataId == latestObjDetails.id &&
                bd.status != 9,
        );

    $: encounterHasNoProvider =
        !patientEncounterDetails.doctorProviderId ||
        !patientEncounterDetails.doctorProvider;

    $: feeEstimateHasValidInsuranceType =
        (latestObjDetails.hasOwnProperty("insuranceType") &&
            latestObjDetails.insuranceType != unknownPatientInsuranceType) ||
        patientEncounterDetails.patient.insuranceType !=
            unknownPatientInsuranceType;

    $: btnOptions = [
        {
            iconName: "addIcon",
            iconFunction: () => _openModifyItemModal(),
            hasTooltip: true,
            isHidden: !showAddItemIcon,
            btnText: itemsData.length ? "Select Items" : "Add",
            iFont: itemsData.length ? editIcon : addIcon,
            tooltip: feeEstimateHasValidInsuranceType
                ? `${__BackupItemsData__.length ? (isBillingDetailsUsingFreeText ? "Change" : "Select") : "Add"} ${isBillingDetailsUsingAsa ? "ASA" : isBillingDetailsUsingFreeText ? "Free Text" : "MBS"} Items`
                : "Select insurance type before add items",
            isDisabled: !feeEstimateHasValidInsuranceType,
        },
        {
            iconName: "addWithoutBillingDataIcon",
            iFont: addIcon,
            iconFunction: () => _openAddItemModalWithoutLatestBillingData(),
            hasTooltip: true,
            btnText: "Add",
            isHidden: showAddItemIcon,
            isDisabled: !feeEstimateHasValidInsuranceType,
            tooltip: feeEstimateHasValidInsuranceType
                ? "Add Items"
                : "Select insurance type before add items",
        },
        {
            iconName: "selectPresetIcon",
            iFont: "fa-solid fa-file-signature",
            iconFunction: () => _openSelectPresetModal(),
            btnText: "Apply Preset",
            // hide preset icon if billingObj has generated estimate/invoice pdf OR
            // encounter billing status is other than 0 OR
            // encounter billing status is 2 but ALL quote-related sections are hidden
            isHidden: patientEncounterDetails.billingStatus != 0 || isLoadingDoctorPresets || isLoadingDoctorBillingRules
        },
        {
            iconName: "billingRuleIcon",
            iFont: "fa-solid fa-wallet",
            iconFunction: () => _openAddBillingRuleModal(),
            hasTooltip: encounterHasNoProvider,
            tooltip:
                "No provider has been set to this encounter, please add one before select billing rule",
            btnText: "Select billing rule",
            isHidden:
                isBillingDetailsUsingFreeText ||
                !__BackupItemsData__.length ||
                itemsNotSaved,
            isDisabled: encounterHasNoProvider,
        },
    ];

    $: MbsColumns = [
        {
            key: "item",
            title: "Item",
            headerClass: "text-left pl-3 cursor-default w-[10%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${getText(v.itemNum)}</p>`,
        },
        {
            key: "type",
            title: "Type",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => getText(v.infoType),
        },
        {
            key: "title",
            title: "Title",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => getText(v.infoShortTitle),
        },
        {
            key: "unitValue",
            title: "Unit value",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            // renderValue: (v) =>
            //     `<p class="${v.isLast ? "font-bold" : ""}">${getText(v.rebateBasicUnits)}</p>`,
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${getText(getMBSRebateUnits(v))}</p>`,
        },
        {
            key: "benefit100",
            title: "MBS-100", // renamed Benefit100 - Medicare Rebate
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.benefit100) : getAmountInCurrency(v.benefit100)}</p>`,
        },
        {
            key: "rebate",
            title: "Rebate", // renamed Rebate - Insurer Rebate
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.rebate) : getAmountInCurrency(v.rebate)}</p>`,
        },
        {
            key: "feeCharged",
            title: "Fee Charged",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.feeCharged) : getAmountInCurrency(v.feeCharged)}</p>`,
        },
        {
            key: "actions",
            title: "",
            headerClass:
                "text-left px-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-default",
            renderValue: (v) =>
                v.isLast ? "" : _renderActionIcons(itemType, v),
        },
    ];

    $: AsaColumns = [
        {
            key: "item",
            title: "Item",
            headerClass: "text-left pl-3 cursor-default w-[10%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${getText(v.itemCode)}</p>`,
        },
        {
            key: "mbsItemCode",
            title: "MBS Code",
            headerClass: "text-left px-3 cursor-default w-[20%]",
            class: "text-left px-3 cursor-default",
            renderValue: (v) =>
                v.mbsItemNum ? getText(v.mbsItemNum) : v.isLast ? "" : "-",
        },
        {
            key: "type",
            title: "Type",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => getText(v.infoType),
        },
        {
            key: "title",
            title: "Title",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) => getText(v.infoShortTitle),
        },
        {
            key: "amaUnits",
            title: "AMA Units",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${getNumber(v.amaUnits)}</p>`,
        },
        {
            key: "rebateAMA",
            title: "Insurer Rebate", // renamed Rebate
            headerClass: "text-left px-3 cursor-default w-[10%] ",
            class: "text-left px-3 cursor-default",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.rebate) : getAmountInCurrency(v.rebate)}</p>`,
        },
        {
            key: "feeCharged",
            title: "Fee Charged",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.feeCharged) : getAmountInCurrency(v.feeCharged)}</p>`,
        },
        {
            key: "itemNote",
            title: "Item Note",
            headerClass: "text-left px-3 cursor-default w-[20%]",
            class: "text-left px-3 cursor-default w-[20%]",
            renderValue: (v) => getText(v.encounterItemDescription),
            isHidden: itemType != "invoice",
        },
        {
            key: "actions",
            title: "",
            headerClass:
                "text-left px-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-default",
            renderValue: (v) =>
                v.isLast ? "" : _renderActionIcons(itemType, v),
        },
    ];

    $: FreeTextColumns = [
        {
            key: "freeTextDescription",
            title: "Item Description",
            headerClass: "text-left pl-3 cursor-default w-[70%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[70%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? "Total" : getText(v.encounterItemDescription)}</p>`,
        },
        {
            key: "freeTextFeeCharged",
            title: "Fee Charged",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl w-[20%]",
            class: "text-left px-3 cursor-default w-[20%]",
            renderValue: (v) =>
                `<p class="${v.isLast ? "font-bold" : ""}">${v.isLast ? getText(v.feeCharged) : getAmountInCurrency(v.feeCharged)}</p>`,
        },
        {
            key: "actions",
            title: "",
            headerClass:
                "text-left px-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-default",
            renderValue: (v) =>
                v.isLast ? "" : _renderActionIcons(itemType, v),
        },
    ];

    let isModifyItemModalOpen = false,
        isAddItemModalWithoutLatestBillingDataModalOpen = false,
        isEditItemModalOpen = false,
        isAddBillingRuleModalOpen = false,
        itemDetails = {
            itemNum: "",
            rebate: Number(0),
            feeCharged: Number(0),
            fieldChange: Number(0),
            encounterItemDescription: "",
            itemType: "",
        },
        defaultItem = {},
        selectedItem = {};

    let billingRuleModalRef;

    let displayItemsData = [],
        mbsTimeData = [];

    let isShowRegenerateModal = false;

    let isShowDeleteModal = false,
        isDeletingItem = false;

    let isUpdatingItems = false;

    let isSelectPresetModalOpen = false,
        selectedPreset = {},
        selectedPresetFeeEstimateRule = {},
        isConfirmSelectPresetModalOpen = false,
        isApplyingPreset = false;

    let doctorPresets = [],
        hasDoctorPresetsFetched = false;
    let isLoadingDoctorPresets = false;

    let doctorBillingRules = [],
        hasDoctorBillingRulesFetched = false;
    let isLoadingDoctorBillingRules = false;

    onMount(async () => {
        setTimeout(() => {
            displayItemsData = _syncLatestDataItemsWithLogic();
        }, 0);

        await loadDoctorPresets();
        await loadDoctorBillingRules();
    });

    const saveBillingItem = async (item, actionType = "add", itemProps) => {
        switch (actionType) {
            case "add":
                isUpdatingItems = true;

                const { billingData } = patientEncounterDetails;

                const { neuraxialType, isUsingAsaItems, isUsingFreeTextItems } =
                    itemProps;

                // Auto generate latest billing data if none
                if (
                    !billingData.length ||
                    !billingData.some((bd) => bd.isLatestQuoteData)
                ) {
                    const billingCodesType = isUsingFreeTextItems
                        ? Number(2)
                        : isUsingAsaItems
                          ? Number(1)
                          : Number(0);

                    patientEncounterDetails =
                        await generateLatestBillingDetails(
                            patientEncounterDetails,
                            itemType,
                            billingCodesType,
                        );

                    await apiMethods.putMethod
                        .updatePatientEncounter(patientEncounterDetails, false, "Save fee estimate item")
                        .then((res) => reloadBillingDetails(false, res));

                    // update itemProps billingDataId as in this case the billingDataId was undefined
                    itemProps.billingDataId = latestObjDetails.id;
                }

                if (isUsingFreeTextItems)
                    await updateFreeTextItems(item, itemProps);
                else {
                    if (!isUsingAsaItems)
                        item = await fetchAndSyncItemsRebate(item);

                    await _handleAddItem(item, itemDetails);

                    if (neuraxialType)
                        patientEncounterDetails.neuraxialType = neuraxialType;

                    _closeModifyItemModal();

                    displayItemsData = _syncLatestDataItemsWithLogic();

                    await updateItems();
                }

                isUpdatingItems = false;

                _closeModifyItemModal();
                break;
            case "edit":
                await _handleEditItem(item);

                _closeEditItemModal();
                break;
            case "delete":
                await _handleDeleteItem(item);

                _closeDeleteModal();
                break;
            default:
                isUpdatingItems = false;
                return;
        }
    };

    const saveBillingRule = () => billingRuleModalRef.updateBillingRule();

    const handleSelectItemToAdd = (type = "") => {
        isBillingDetailsUsingAsa = type == "ASA";
        isBillingDetailsUsingFreeText = type == "FreeText";

        _closeAddItemModalWithoutLatestBillingData();
        _openModifyItemModal();
    };

    export const updateItems = async () => {
        if (isBillingDetailsUsingAsa) {
            latestObjDetails.asaData = displayItemsData
                .filter((item) => item.itemCode != "Time")
                .map((item) => {
                    delete item.id;
                    return {
                        ...item,
                        encounterItemDescription: "",
                        amaCode: item.itemCode,
                    };
                });
            if (displayItemsData.some((item) => item.itemCode == "Time")) {
                const timeCode = displayItemsData.find(
                    (item) => item.itemCode == "Time",
                );

                latestObjDetails.asaTimeData = [{ ...timeCode }];
            } else latestObjDetails.asaTimeData = [];
        } else latestObjDetails.mbsData = [...displayItemsData];

        patientEncounterDetails.billingData.map((bd) => {
            if (bd.id == latestObjDetails.id) return latestObjDetails;
            else return bd;
        });

        isUpdatingItems = true;

        await apiMethods.putMethod
            .updatePatientEncounter({ ...patientEncounterDetails }, false, "Update fee estimate items")
            .then(async (res) => {
                if (res.error) showError(res.error);
                else await handleOpenModal(true, res);
            })
            .catch((err) => showException(err));

        __BackupItemsData__ = [...itemsData];

        isUpdatingItems = false;
    };

    const updateFreeTextItems = async (items = [], itemProps = {}) => {
        const { billingDataId } = itemProps;

        const existingItems = __BackupItemsData__;
        const itemsMap = new Map(items.map((item) => [item.id, item]));

        // handle remove items
        const deletedItems = existingItems.filter(
            (backupItem) => !itemsMap.has(backupItem.id),
        );
        if (deletedItems.length > 0)
            for (const item of deletedItems)
                await apiMethods.deleteMethod
                    .deleteFreeTextItem({
                        ...item,
                        billingCodesType: Number(2),
                        patientEncounterId: patientEncounterDetails.id,
                        patientEncounterBillingDataId: billingDataId,
                    })
                    .then((res) => {
                        if (!res || res.error)
                            showError(
                                res.error ??
                                    "Failed to delete free text item, please refresh the page and try again and contact support if the issue persists.",
                            );
                    });

        // handle new items
        const newItems = items.filter((item) => item.hasOwnProperty("tempId"));
        if (newItems.length > 0)
            for (const item of newItems)
                await apiMethods.postMethod
                    .createFreeTextItem({
                        ...item,
                        billingCodesType: Number(2),
                        patientEncounterId: patientEncounterDetails.id,
                        patientEncounterBillingDataId: billingDataId,
                    })
                    .then((res) => {
                        if (!res || res.error)
                            showError(
                                res.error ??
                                    "Failed to create free text item, please refresh the page and try again and contact support if the issue persists.",
                            );
                    });

        // handle update items
        const modifiedItems = items.filter((item) => {
            const backupItem = existingItems.find(
                (backup) => backup.id === item.id,
            );
            return (
                backupItem &&
                (backupItem.encounterItemDescription !=
                    item.encounterItemDescription ||
                    backupItem.feeCharged != item.feeCharged)
            );
        });
        if (modifiedItems.length > 0)
            for (const item of modifiedItems)
                await apiMethods.putMethod
                    .updateFreeTextItem({
                        ...item,
                        billingCodesType: Number(2),
                        patientEncounterId: patientEncounterDetails.id,
                        patientEncounterBillingDataId: billingDataId,
                    })
                    .then((res) => {
                        if (!res || res.error)
                            showError(
                                res.error ??
                                    "Failed to update free text item, please refresh the page and try again and contact support if the issue persists.",
                            );
                    });

        await reloadBillingDetails();
        __BackupItemsData__ = [...items];
        displayItemsData = _syncLatestDataItemsWithLogic();
    };

    const handleOpenModal = async (isItemChange = false, response = {}) => {
        if (encounterHasNoProvider) return;

        await reloadBillingDetails(false, response);

        const { insuranceType, billingType } = latestObjDetails;

        if (
            insuranceTypesNotAllowBillingRule.includes(insuranceType) ||
            billingType
        )
            await confirmRegenerate({ isRecalculate: true, isItemChange });
        else {
            // Sync display items to match api calculation result
            displayItemsData = _syncLatestDataItemsWithLogic();
            displayItemsData = await _syncAsaTimeData(displayItemsData);

            await regenerateQuote(
                latestObjDetails,
                true,
                itemType,
                isItemChange,
                true,
            );

            _openAddBillingRuleModal();
        }

        // await updateBillingRuleTotalFee(
        //     patientEncounterDetails,
        //     latestObjDetails,
        // );

        await reloadBillingDetails(false, patientEncounterDetails);
    };

    const confirmRegenerate = async ({
        isRecalculate = false,
        isItemChange = false,
        skipRegenerateAndCalculate = false,
    }) => {
        await regenerateQuote(
            latestObjDetails,
            isRecalculate,
            itemType,
            isItemChange,
            skipRegenerateAndCalculate,
        );

        // Sync display items to match api calculation result
        displayItemsData = _syncLatestDataItemsWithLogic();
        displayItemsData = await _syncAsaTimeData(displayItemsData);

        _closeRegenerateModal();
    };

    export const syncDisplayItems = () => {
        displayItemsData = _syncLatestDataItemsWithLogic();
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "actions") {
            selectedItem = { ...rowData };

            if (className.includes("addIcon")) _openModifyItemModal(false);
            else if (className.includes("deleteIcon")) _openDeleteModal();
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const changeItemType = async (billingCodesType = Number(0)) => {
        isBillingDetailsUsingAsa = billingCodesType == 1;
        isBillingDetailsUsingFreeText = billingCodesType == 2;

        _closeModifyItemModal();

        // update billing data if exists
        if (latestObjDetails.id) {
            patientEncounterDetails.billingData =
                patientEncounterDetails.billingData.map((bd) => {
                    if (
                        bd.id == latestObjDetails.id &&
                        itemType == "quote" &&
                        bd.isLatestQuoteData
                    )
                        return {
                            ...bd,
                            asaData: [],
                            mbsData: [],
                            freeTextData: [],
                            asaTimeData: null,
                            actualGap: Number(0),
                            rebate: Number(0),
                            totalFee: Number(0),
                            useAsaCodes: isBillingDetailsUsingAsa,
                            billingCodesType,
                        };
                    else return bd;
                });

            patientEncounterDetails = updateEncounterLatestBillingData(
                patientEncounterDetails,
            );

            await apiMethods.putMethod
                .updatePatientEncounter(patientEncounterDetails, false, "Change fee estimate items type")
                .then(async (res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to change item type, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else await reloadBillingDetailsAndUpdateDefaultItems();
                })
                .catch((err) => showException(err));
        }

        _openModifyItemModal();
    };

    // HELPER FUNCTIONS
    const _handleAddItem = async (items = [], itemDetails) => {
        items = items.map((item) => ({
            ...item,
            feeCharged: itemDetails.isUsingAsaItems
                ? item.rebateAMA
                : item.benefit100,
            rebate: itemDetails.isUsingAsaItems
                ? item.rebateAMA
                : item.benefit100,
            // fieldChange: 0,
        }));

        itemsData = [...items];
    };

    const _handleEditItem = async (item) => {
        const {
            isUsingAsaItems,
            isUsingFreeTextItems,
            encounterItemDescription,
        } = item;

        const { id: encounterId } = patientEncounterDetails;

        const { id: billingDataId } = latestObjDetails;

        isUpdatingItems = true;

        if (isUsingAsaItems) {
            const { itemCode, subItemCode } = item;

            const data = {
                encounterId,
                billingDataId,
                itemCode,
                subItemCode,
                encounterItemDescription,
            };

            await apiMethods.putMethod
                .updateEncounterAsaItemDescription(data)
                .then(async (res) => {
                    if (res.error) showError(res.error);
                    else {
                        await reloadBillingDetails();
                        __BackupItemsData__ = [...itemsData];
                    }
                })
                .catch((err) => showException(err));
        } else if (isUsingFreeTextItems)
            await apiMethods.putMethod
                .updateFreeTextItem({
                    ...item,
                    billingCodesType: Number(2),
                    patientEncounterId: encounterId,
                    patientEncounterBillingDataId: billingDataId,
                })
                .then(async (res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to update selected item, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else {
                        await reloadBillingDetails();
                        __BackupItemsData__ = [...itemsData];
                        displayItemsData = _syncLatestDataItemsWithLogic();
                    }
                })
                .catch((err) => showException(err));
        else {
            const { itemNum, subItemNum } = item;

            const data = {
                encounterId,
                billingDataId,
                itemNum,
                subItemNum,
                encounterItemDescription,
            };

            await apiMethods.putMethod
                .updateEncounterMbsItemDescription(data)
                .then(async (res) => {
                    if (res.error) showError(res.error);
                    else {
                        await reloadBillingDetails();
                        __BackupItemsData__ = [...itemsData];
                    }
                })
                .catch((err) => showException(err));
        }

        isUpdatingItems = false;
    };

    const _handleDeleteItem = async (item) => {
        isDeletingItem = true;

        const isUsingFreeTextItems =
            item.id &&
            !item.hasOwnProperty("itemCode") &&
            !item.hasOwnProperty("itemNum");

        if (isUsingFreeTextItems) {
            await apiMethods.deleteMethod
                .deleteFreeTextItem({
                    ...item,
                    billingCodesType: Number(2),
                    patientEncounterId: patientEncounterDetails.id,
                    patientEncounterBillingDataId: latestObjDetails.id,
                })
                .then(async (res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to remove selected item, please try again later or contact uspport...",
                        );
                    else {
                        await reloadBillingDetails();
                        __BackupItemsData__ = [...itemsData];
                    }
                })
                .catch((err) => showException(err));
        } else {
            const isAsaItem = item.hasOwnProperty("itemCode");

            if (!isAsaItem)
                itemsData = itemsData.filter(
                    (data) =>
                        data.itemNum != item.itemNum ||
                        data.subItemNum != item.subItemNum,
                );
            else
                itemsData = itemsData.filter(
                    (data) =>
                        data.itemCode != item.itemCode ||
                        data.amaCode != item.amaCode,
                );

            if (isAsaItem && item.itemCode == "Time") {
                patientEncounterDetails.billingData =
                    patientEncounterDetails.billingData.map((bd) => {
                        if (bd.id != latestObjDetails.id) return { ...bd };
                        else return { ...bd, asaTimeData: null };
                    });
            }
        }

        isDeletingItem = false;
    };

    const _syncLatestDataItemsWithLogic = () => {
        const itemMap = {};
        const updatedItemsData = itemsData.map((item) => ({ ...item }));

        if (isBillingDetailsUsingAsa) {
            for (const mapping of $asaItemsLogic) {
                itemMap[mapping.hideItem] = mapping.targetItem;
            }

            return updatedItemsData.filter((item) => {
                const hideItem = item.itemCode?.toString();
                if (itemMap[hideItem] !== undefined) {
                    const targetItem = updatedItemsData.find(
                        (item) => item.itemCode === itemMap[hideItem],
                    );
                    if (targetItem) {
                        targetItem.feeCharged += item.feeCharged;
                        targetItem.rebate += item.rebate;
                        return false;
                    }
                    return true;
                }
                return true;
            });
        } else {
            for (const mapping of $mbsItemsLogic) {
                itemMap[mapping.hideItem] = mapping.targetItem;
            }

            return updatedItemsData.filter((item) => {
                const hideItem = item.itemNum?.toString();
                if (itemMap[hideItem] !== undefined) {
                    const targetItem = updatedItemsData.find(
                        (item) => item.itemNum === itemMap[hideItem],
                    );
                    if (targetItem) {
                        targetItem.benefit100 += item.benefit100;
                        targetItem.feeCharged += item.feeCharged;
                        targetItem.rebate += item.rebate;
                        return false;
                    }
                    return true;
                }
                return true;
            });
        }
    };

    const _syncAsaTimeData = async (arr) => {
        if (!arr.some((item) => item.itemCode == "Time")) return arr;

        if (!mbsTimeData.length)
            await apiMethods.getMethod
                .getMBSItems(0, 999999, {
                    "filter[infoType]": "Time",
                })
                .then((res) => {
                    if (res.results?.length) mbsTimeData = [...res.results];
                });

        const timeItem = arr.find((item) => item.itemCode == "Time");

        const targetItem = mbsTimeData.find(
            (td) => td.basicUnits == timeItem.units,
        );

        if (targetItem) timeItem.mbsItemNum = targetItem.itemNum;

        return arr.map((item) => {
            if (item.itemCode == "Time") return timeItem;
            else return item;
        });
    };

    const _renderActionIcons = (itemType = "", data) => {
        if (isBillingDetailsUsingFreeText)
            return `<div class="flex flex-1 items-center justify-start gap-x-2">
                        <div class="addIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit item"}">
                            <i class="addIcon ${editIcon}"></i>
                        </div>

                        <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Remove item"}">
                            <i class="deleteIcon ${deleteIcon}"></i>
                        </div>
                    </div>`;

        if (!itemType || itemType.toLowerCase() == "quote") return "";

        const { encounterItemDescription } = data;

        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="addIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="${encounterItemDescription ? "Edit" : "Add"} item note">
                        ${
                            encounterItemDescription
                                ? `
                            <i class="addIcon ${editIcon}"></i>
                        `
                                : `
                            <i class="addIcon ${addIcon}"></i>
                        `
                        }
                    </div>
                </div>`;
    };

    const _resetDefaultBillingItem = () => {
        const patientAge = _getPatientAge();

        itemDetails = {
            itemNum: "",
            rebate: Number(0),
            feeCharged: Number(0),
            fieldChange: Number(0),
            encounterItemDescription: "",
            encounterId: patientEncounterDetails.id,
            billingDataId: latestObjDetails.id,
            isUsingAsaItems: isBillingDetailsUsingAsa,
            isUsingFreeTextItems: isBillingDetailsUsingFreeText,
            billingType: patientEncounterDetails.typeOfBill,
            itemType,
            patientAge,
            patientState: patientEncounterDetails.patient?.state ?? "",
            neuraxialType: patientEncounterDetails.neuraxialType,
            mbsItem22012UsedToday:
                patientEncounterDetails.mbsItem22012UsedToday,
            mbsItem22014UsedToday:
                patientEncounterDetails.mbsItem22014UsedToday,
            procedureName: patientEncounterDetails.procedureName,
        };
        defaultItem = {};
        selectedItem = {};
    };

    const _openModifyItemModal = (isNew = true) => {
        const patientAge = _getPatientAge();

        if (isNew) {
            const isWcOrTacInsurance =
                insuranceTypesMayAllowAmaBillingRule.includes(
                    latestObjDetails.insuranceType,
                );
            const isWcOrTacUsingAmaRates =
                latestObjDetails.usingAmaRates ?? true;
            const shouldUseDynamicAmaRates =
                isWcOrTacInsurance && isWcOrTacUsingAmaRates;

            itemDetails = {
                ...itemDetails,
                encounterId: patientEncounterDetails.id,
                billingDataId: latestObjDetails.id,
                isUsingAsaItems: isBillingDetailsUsingAsa,
                isUsingFreeTextItems: isBillingDetailsUsingFreeText,
                billingType: patientEncounterDetails.typeOfBill,
                itemType,
                patientAge,
                patientState: patientEncounterDetails.patient?.state ?? "",
                neuraxialType: patientEncounterDetails.neuraxialType,
                shouldUseDynamicAmaRates,
                currentCalculationAmount:
                    latestObjDetails.calculationAmount ?? $amaUnitValue,
                mbsItem22012UsedToday:
                    patientEncounterDetails.mbsItem22012UsedToday,
                mbsItem22014UsedToday:
                    patientEncounterDetails.mbsItem22014UsedToday,
                procedureName: patientEncounterDetails.procedureName,
            };

            const { startDate, endDate } = latestObjDetails;

            if (startDate) itemDetails = { ...itemDetails, startDate };

            if (endDate) itemDetails = { ...itemDetails, endDate };

            isModifyItemModalOpen = true;
        } else {
            itemDetails = {
                ...selectedItem,
                encounterId: patientEncounterDetails.id,
                billingDataId: latestObjDetails.id,
                isUsingAsaItems: isBillingDetailsUsingAsa,
                isUsingFreeTextItems: isBillingDetailsUsingFreeText,
            };
            defaultItem = { ...itemDetails };
            isEditItemModalOpen = true;
        }
    };

    const loadDoctorPresets = async () => {
        if (doctorPresets.length || hasDoctorPresetsFetched) return;

        isLoadingDoctorPresets = true;

        await apiMethods.getMethod
            .getAccountPresets(0, 999999, {
                "filter[doctorId]": patientEncounterDetails.doctorId,
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load doctor presets, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    doctorPresets = [...res.results];
                    hasDoctorPresetsFetched = true;
                }
            })
            .catch((err) => showError(err));

        isLoadingDoctorPresets = false;
    };

    const loadDoctorBillingRules = async () => {
        if (doctorBillingRules.length || hasDoctorBillingRulesFetched) return;

        isLoadingDoctorBillingRules = true;

        await apiMethods.getMethod
            .getBillingRules(0, 999999, {
                "filter[doctorId]": patientEncounterDetails.doctorId,
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load doctor billing rules, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    doctorBillingRules = [...res.results];
                    hasDoctorBillingRulesFetched = true;
                }
            })
            .catch((err) => showError(err));

        isLoadingDoctorBillingRules = false;
    };

    const _openSelectPresetModal = () => {
        isSelectPresetModalOpen = true;
        //_openModifyItemModal(true, false);
    };

    const _openConfirmSelectPresetModal = () =>
        (isConfirmSelectPresetModalOpen = true);

    const _closeSelectPresetModal = () => {
        isSelectPresetModalOpen = false;
        isConfirmSelectPresetModalOpen = false;
        selectedPreset = {};
    };

     const handleSelectPreset = async () => {
        // show confirm modal if billing data has mbs items already
        if (
            latestObjDetails.id &&
            latestObjDetails.billingCodesType != 1 &&
            latestObjDetails.mbsData?.length > 0
        )
            _openConfirmSelectPresetModal();
        else await confirmSelectPreset();
    };

    const confirmSelectPreset = async () => {
        isApplyingPreset = true;

        await apiMethods.putMethod
            .applyPresetToEncounter(
                patientEncounterDetails.id,
                latestObjDetails.id,
                selectedPreset.id,
            )
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to apply preset, please refresh the page and try again and contact support if the issue persists.",
                    );
            });

        isApplyingPreset = false;

        await reloadBillingDetailsAndUpdateDefaultItems();

        displayItemsData = _syncLatestDataItemsWithLogic();

        _closeSelectPresetModal();
    };

    const _openAddItemModalWithoutLatestBillingData = () => {
        if (allowAsaCodes || allowFreeTextCodes)
            isAddItemModalWithoutLatestBillingDataModalOpen = true;
        else handleSelectItemToAdd("MBS");
    };

    const _checkAllowAsaCodes = (encounter = {}) => {
        if (!encounter || isObjectEmpty(encounter)) return false;

        const {
            patient: { insuranceType },
        } = encounter;

        return insuranceType == 10 || insuranceType == 15;
    };

    const _closeAddItemModalWithoutLatestBillingData = () => {
        isAddItemModalWithoutLatestBillingDataModalOpen = false;

        itemDetails = {
            itemNum: "",
            rebate: Number(0),
            feeCharged: Number(0),
            fieldChange: Number(0),
            encounterItemDescription: "",
            itemType: "",
        };
    };

    const _openAddBillingRuleModal = () => (isAddBillingRuleModalOpen = true);

    const _getPatientAge = () => {
        const { patient } = patientEncounterDetails;
        const birthDate = new Date(patient.birthDate);
        const currentDate = new Date();

        let age = currentDate.getFullYear() - birthDate.getFullYear();

        if (
            currentDate.getMonth() < birthDate.getMonth() ||
            (currentDate.getMonth() === birthDate.getMonth() &&
                currentDate.getDate() < birthDate.getDate())
        ) {
            age--;
        }

        return age;
    };

    const _closeModifyItemModal = () => {
        isModifyItemModalOpen = false;

        _resetDefaultBillingItem();
    };

    const _closeEditItemModal = () => {
        isEditItemModalOpen = false;

        _resetDefaultBillingItem();
    };

    const _closeAddBillingRuleModal = async (response = {}) => {
        isAddBillingRuleModalOpen = false;

        if (!isObjectEmpty(response)) {
            await reloadBillingDetails(false, response);
            // await reloadBillingDetailsAndUpdateDefaultItems();
            // await updateBillingRuleTotalFee(
            //     patientEncounterDetails,
            //     latestObjDetails,
            // );
            // _openRegenerateModal()
            confirmRegenerate({ skipRegenerateAndCalculate: true });
        }
    };

    const _openRegenerateModal = () => (isShowRegenerateModal = true);

    const _closeRegenerateModal = async (shouldReload = false) => {
        isShowRegenerateModal = false;

        if (shouldReload) await reloadBillingDetails();

        __BackupItemsData__ = [...itemsData];
    };

    const _openDeleteModal = () => (isShowDeleteModal = true);

    const _closeDeleteModal = () => {
        isShowDeleteModal = false;

        _resetDefaultBillingItem();
    };
</script>

<CardLayout title="Fee Estimate Items" {btnOptions}>
    {#if __BackupItemsData__.length}
        {#if isRegenerating}
            <PageLoading />
        {:else}
            <div class="w-full overflow-x-auto">
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={[
                        ...displayItemsData,
                        getSumOfColumnValues(
                            displayItemsData,
                            latestObjDetails.addGST,
                        ),
                    ]}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    {:else}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <!-- <p>No items currently under this Quote</p> -->
            </div>
        </div>
    {/if}
</CardLayout>

<NotificationModal
    title="Notice"
    isModalOpen={isConfirmSelectPresetModalOpen}
    closeModal={_closeSelectPresetModal}
    confirmAction={confirmSelectPreset}
    cancelText="Cancel"
    zIndex={10001}
    submitText="Confirm"
    closeModalFunction={_closeSelectPresetModal}
>
    <div class="p-6">
        <p class="leading-6">
            We've noticed you already have some MBS items under this {itemType},
            to confirm this action it will replace all existing items into
            preset items.
        </p>
        <p class="leading-6">Do you want to proceed?</p>
    </div>
</NotificationModal>

<NotificationModal
    title="Select preset to apply"
    isModalOpen={isSelectPresetModalOpen}
    closeModal={_closeSelectPresetModal}
    isConfirming={isApplyingPreset}
    cancelText="Cancel"
    showConfirmBtn={false}
    closeModalFunction={_closeSelectPresetModal}
    btnOptions={[
        {
            text: "Select",
            loading: isObjectEmpty(selectedPreset) || isApplyingPreset,
            function: () => handleSelectPreset(),
        },
    ]}
>
    <div class="flex flex-1 flex-col gap-y-4">
        {#if !doctorPresets || !doctorPresets.length}
            No presets found
        {:else}
            <div class="flex items-center w-full">
                <select
                    class="input input-bordered input-sm flex-1"
                    on:change|preventDefault={async (e) => {
                        const {
                            target: { value },
                        } = e;
                        selectedPresetFeeEstimateRule = {}
                        selectedPreset =
                            doctorPresets.find(
                                (preset) => preset.id == value,
                            ) ?? {};

                        if (selectedPreset.useAsaCodes) {
                            await cacheAsaItems();
                            selectedPreset.asaCodes =
                                selectedPreset.asaCodes.map((code) => {
                                    if (code.tooltip) return code;

                                    const targetCode = $asaAllItemsList.find(
                                        (item) =>
                                            item.itemCode == code.itemCode &&
                                            item.subItemCode ==
                                                code.subItemCode,
                                    );

                                    return {
                                        ...code,
                                        tooltip: targetCode
                                            ? targetCode.description
                                            : "",
                                    };
                                });
                        } else if (
                            selectedPreset?.hasOwnProperty("useAsaCodes") &&
                            !selectedPreset.useAsaCodes
                        ) {
                            await cacheMbsItems();
                            selectedPreset.mbsCodes =
                                selectedPreset.mbsCodes.map((code) => {
                                    if (code.tooltip) return code;

                                    const targetCode = $mbsAllItemsList.find(
                                        (item) =>
                                            item.itemNum == code.itemNum &&
                                            item.subItemNum == code.subItemNum,
                                    );

                                    return {
                                        ...code,
                                        tooltip: targetCode
                                            ? targetCode.description
                                            : "",
                                    };
                                });
                        }

                        selectedPreset.billingRules =
                            selectedPreset.billingRules.map((br) => {
                                if (br.tooltip) return br;

                                const targetDoctorBillingRule =
                                    doctorBillingRules.find(
                                        (dbr) => dbr.id == br.billingRuleId,
                                    );

                                return {
                                    ...br,
                                    tooltip: targetDoctorBillingRule
                                        ? `${getOOPType(targetDoctorBillingRule.oopType)} ${getAmountInCurrency(targetDoctorBillingRule.amount)} ${getBillingRuleType(targetDoctorBillingRule.type)}`
                                        : "",
                                };
                            });

                        if(selectedPreset && selectedPreset.billingRules?.length) {
                            //get applicable rule for this encounter
                            selectedPresetFeeEstimateRule = await getSelectedPresetApplicableRuleForEncounter(selectedPreset, patientEncounterDetails, latestObjDetails);
                        }
                    }}
                >
                    <option value="" selected disabled>
                        Select preset to apply
                    </option>
                    {#each doctorPresets.filter((p) => {
                        if (!latestObjDetails.hasOwnProperty("billingCodesType") || latestObjDetails.billingCodesType == 2) return p;
                        else if (latestObjDetails.billingCodesType == 0) return !p.useAsaCodes;
                        else return p.useAsaCodes;
                    }) as preset}
                        <option value={preset.id}>{preset.name}</option>
                    {/each}
                </select>
            </div>

            {#if !isObjectEmpty(selectedPreset)}

                <div class="flex items-center w-full gap-x-4">
                    <p class="title">Items numbers:</p>

                    <div class="flex flex-wrap flex-1 gap-x-2">
                        {#each selectedPreset[selectedPreset.useAsaCodes ? "asaCodes" : "mbsCodes"] as item}
                            <div
                                class="cursor-default hover:font-bold tooltip tooltip-secondary"
                                data-tip={item.tooltip ?? ""}
                            >
                                {item[
                                    selectedPreset.useAsaCodes
                                        ? "itemCode"
                                        : "itemNum"
                                ]}{item[
                                    selectedPreset.useAsaCodes
                                        ? "subItemCode"
                                        : "subItemNum"
                                ]
                                    ? `-${item[selectedPreset.useAsaCodes ? "subItemCode" : "subItemNum"]}`
                                    : ""}
                            </div>
                        {/each}
                        {#if selectedPreset.procedureLength}
                            <div
                                class="cursor-default hover:font-bold tooltip tooltip-secondary"
                                data-tip={`${selectedPreset.procedureLength} minutes`}
                            >
                                Time
                            </div>
                        {/if}
                    </div>
                </div>

                <div
                    class="flex flex-col gap-y-4 items-center w-full gap-x-4 max-h-[300px] overflow-y-auto"
                >
                     {#if !isObjectEmpty(selectedPresetFeeEstimateRule)}
                        <div
                            class="flex flex-col gap-y-2 w-full cursor-default"
                        >

                            <div class="flex w-full items-center gap-x-4">
                                <p class="title">Provider:</p>
                                <p>
                                    {doctorProviders.find(
                                        (provider) =>
                                            provider.id ==
                                            selectedPresetFeeEstimateRule.doctorProviderId,
                                    )?.number ?? "-"}
                                </p>
                            </div>

                            <div class="flex w-full items-center gap-x-4">
                                <p class="title">Billing rule:</p>
                                <p
                                    class="cursor-default hover:font-bold tooltip tooltip-secondary"
                                    data-tip={selectedPresetFeeEstimateRule.tooltip ?? ""}
                                >
                                    {doctorBillingRules.find(
                                        (br) =>
                                            br.id == selectedPresetFeeEstimateRule.billingRuleId,
                                    )?.name ?? "-"}
                                </p>
                            </div>
                        </div>
                    {/if}
                </div>
            {/if}
        {/if}
    </div>
</NotificationModal>

{#if isModifyItemModalOpen}
    {#if isBillingDetailsUsingAsa}
        <CodeSelectorAsa
            isSelectorOpen={isModifyItemModalOpen}
            {itemType}
            procedureName={patientEncounterDetails.procedureName}
            doctorId={patientEncounterDetails.doctorId}
            itemCodeObj={itemDetails}
            {itemsData}
            {hasValidBillingData}
            closeModal={_closeModifyItemModal}
            saveBillingItem={(...args) => saveBillingItem(...args)}
            {changeItemType}
            isSavingItems={isUpdatingItems}
            {allowFreeTextCodes}
        />
    {:else if isBillingDetailsUsingFreeText}
        <CodeSelectorFreeText
            isSelectorOpen={isModifyItemModalOpen}
            itemType={itemType}
            procedureName={patientEncounterDetails.procedureName}
            itemCodeObj={itemDetails}
            {itemsData}
            {hasValidBillingData}
            closeModal={_closeModifyItemModal}
            saveBillingItem={(...args) => saveBillingItem(...args)}
            {changeItemType}
            {allowAsaCodes}
            isSavingItems={isUpdatingItems}
        />
    {:else}
        <CodeSelectorMbs
            isSelectorOpen={isModifyItemModalOpen}
            {itemType}
            doctorId={patientEncounterDetails.doctorId}
            procedureName={patientEncounterDetails.procedureName}
            itemCodeObj={itemDetails}
            {itemsData}
            {hasValidBillingData}
            closeModal={_closeModifyItemModal}
            saveBillingItem={(...args) => saveBillingItem(...args)}
            {changeItemType}
            isSavingItems={isUpdatingItems}
            {allowAsaCodes}
            {allowFreeTextCodes}
        />
    {/if}
{/if}

{#if isEditItemModalOpen}
    <PatientEncounterModifyItemModal
        isModifyItemModalOpen={isEditItemModalOpen}
        {isUpdatingItems}
        {itemDetails}
        {defaultItem}
        {itemsData}
        {saveBillingItem}
        closeModal={_closeEditItemModal}
    />
{/if}

{#if isAddBillingRuleModalOpen}
    <PatientEncounterAddBillingRuleModal
        bind:this={billingRuleModalRef}
        {isAddBillingRuleModalOpen}
        closeModal={_closeAddBillingRuleModal}
        isAsaItems={isBillingDetailsUsingAsa}
        {itemsData}
        {latestObjDetails}
        {patientEncounterDetails}
        isFromQuoteOnlyEncounter
        saveBillingRule={(...args) => saveBillingRule(...args)}
    />
{/if}

<NotificationModal
    isModalOpen={isShowRegenerateModal}
    isConfirming={isRegenerating}
    closeModal={() => _closeRegenerateModal(true)}
    showConfirmBtn={false}
    closeModalFunction={() => _closeRegenerateModal(true)}
    btnOptions={[
        {
            text: isRegenerating ? "Generating" : "Confirm",
            loading: isRegenerating,
            function: () => confirmRegenerate({ isRecalculate: true }),
        },
    ]}
>
    Please confirm you would like to update the item numbers and re-calculate
    the associated billing details.
</NotificationModal>

<NotificationModal
    title="Remove Item"
    isModalOpen={isShowDeleteModal}
    isConfirming={isDeletingItem}
    closeModal={() => saveBillingItem(selectedItem, "delete")}
    confirmAction={_closeDeleteModal}
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    cancelText={isDeletingItem ? "Deleting" : "Delete"}
    closeModalFunction={_closeDeleteModal}
    isReverseBtns
>
    <p class="whitespace-pre-line">Remove this item?</p>
</NotificationModal>

<NotificationModal
    title="Select Items type"
    isModalOpen={isAddItemModalWithoutLatestBillingDataModalOpen}
    closeModal={_closeAddItemModalWithoutLatestBillingData}
    showConfirmBtn={false}
    closeModalFunction={_closeAddItemModalWithoutLatestBillingData}
>
    <div class="flex flex-col flex-1 gap-y-2">
        <div class="flex flex-1 gap-x-2 items-center justify-around">
            <button
                class="btn btn-md w-36 btn-secondary"
                on:click|preventDefault={() => handleSelectItemToAdd("MBS")}
            >
                MBS
            </button>

            {#if allowAsaCodes}
                <button
                    class="btn btn-md w-36 btn-secondary"
                    on:click|preventDefault={() => handleSelectItemToAdd("ASA")}
                >
                    ASA
                </button>
            {/if}

            {#if allowFreeTextCodes}
                <button
                    class="btn btn-md w-36 btn-secondary"
                    on:click|preventDefault={() =>
                        handleSelectItemToAdd("FreeText")}
                >
                    Free Text
                </button>
            {/if}
        </div>
    </div>
</NotificationModal>
